import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Flex,
  Skeleton,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useMemo } from "react";
import { LazyList } from "src/components/Layout/LazyRender";
import { FILE_UPLOAD_FILTER_OPTIONS } from "src/constants";
import { useSchoolAdmin } from "src/hooks/useSchoolAdmin";
import * as FormTemplate from "src/services/formTemplate";
import {
  getFormCompleteQuestions,
  QuestionFilter,
} from "src/services/formTemplate/question";
import * as AF from "src/types/formTemplate";
import * as GQL from "src/types/graphql";
import { RemoteData } from "src/types/remoteData";
import { CommonFilterProps } from "./CommonFilters";
import { DynamicQuestionFilter, QuestionFilterProps } from "./filters";

type FormFiltersAdvancedToolsProps = {
  formTemplateRemoteData: RemoteData<Error, GQL.FormTemplateFragment>;
} & CommonFilterProps;

const defaultDynamicQuestionFiltersLimit = 10;

export const FormFiltersAdvancedTools: React.FC<
  FormFiltersAdvancedToolsProps
> = ({ formTemplateRemoteData, ...props }) => {
  const { isSchoolAdmin, allSchoolIds } = useSchoolAdmin();
  const allDynamicQuestionFilters = useMemo(() => {
    const filter: QuestionFilter = isSchoolAdmin
      ? { filterBy: "school", schoolIdSet: new Set(allSchoolIds) }
      : { filterBy: "none" };
    return formTemplateRemoteData
      .map(FormTemplate.fromGQL)
      .map((formTemplate) => formatDynamicQuestionFilters(formTemplate, filter))
      .withDefault([]);
  }, [allSchoolIds, formTemplateRemoteData, isSchoolAdmin]);

  return (
    <Accordion allowToggle>
      <AccordionItem
        border="none"
        sx={{
          "& > .chakra-collapse": {
            overflow: "visible !important",
          },
        }}
      >
        {({ isExpanded }) => {
          return (
            <>
              <AccordionButton paddingX="0">
                <Text variant="filterLabel" flex="1" textAlign="left">
                  Advanced Tools
                </Text>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel p="0" mt={4}>
                <VStack align="inherit">
                  {isExpanded && (
                    <LazyList
                      elements={allDynamicQuestionFilters}
                      chunkSize={defaultDynamicQuestionFiltersLimit}
                      placeholder={PlaceholderSkeleton}
                    >
                      {(chunk) =>
                        chunk.map((questionFilter) => (
                          <DynamicQuestionFilter
                            key={questionFilter.questionId}
                            {...questionFilter}
                            {...props}
                          />
                        ))
                      }
                    </LazyList>
                  )}
                </VStack>
              </AccordionPanel>
            </>
          );
        }}
      </AccordionItem>
    </Accordion>
  );
};

const PlaceholderSkeleton = () => (
  <Flex direction="column" gap="4" alignItems="center" mt={2} height="100vh">
    <Skeleton height="2rem" width="100%" borderRadius="md" />
    <Skeleton height="2rem" width="100%" borderRadius="md" />
    <Skeleton height="2rem" width="100%" borderRadius="md" />
  </Flex>
);

export const formatDynamicQuestionFilters = (
  formTemplate: AF.FormTemplate<AF.WithId>,
  filter: QuestionFilter
) => {
  return getFormCompleteQuestions(formTemplate, filter)
    .filter((q) => {
      // Filter out archived questions
      if (q.isArchived) {
        return false;
      }
      // Filter to only include SingleSelect, MultiSelect, and FileUpload question types
      return [
        AF.SingleSelectType,
        AF.MultiSelectType,
        AF.FileUploadType,
      ].includes(q.type);
    })
    .map<QuestionFilterProps>((question) => ({
      questionId: question.id,
      questionLabel: question.question,
      options:
        question.type === AF.FileUploadType
          ? FILE_UPLOAD_FILTER_OPTIONS
          : "options" in question
          ? question.options
          : [],
    }));
};
